import { useId } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { LockClosedIcon, LockOpenIcon } from "./lock-icons";

/**
 * THE PIECES A PER-ROW CRITERION DRILL-DOWN IS BUILT FROM, shared by the PERSON ranking
 * (`CandidateRankingPanel`) and the COMPANY ranking (`CompanyRankingPanel`).
 *
 * Extracted rather than copied (Kevin, 2026-09-09): the two screens were held side by side and the
 * company drill-down was a flat list where the person one has filters, kind groups and a status dot
 * per line. Copying these four pieces would have put two definitions of "what an amber dot means"
 * into one folder, and the whole point of the request is that the two screens agree — so they are
 * one definition, in the same spirit as `RankingThresholdSlider`, `RankingInsightsPanel`,
 * `RankingBandGroups` and `ReadinessList`.
 *
 * WHAT LIVES HERE: the pieces whose LOOK has to be identical — the status dot's three colours,
 * the Muss/Kann lock badge, the collapsible kind heading, and the requirement filter with its two
 * lock glyphs, plus the expandable AI explanation.
 *
 * WHAT DELIBERATELY DOES NOT: which bucket a criterion falls into, and what the OUTCOME filter's
 * buckets are called. Those differ by subject and cannot be shared without one side lying about the
 * other. A person criterion can be "kulant erfüllt" — matched by a lenient level check — and a
 * company criterion cannot, because company expertise has been binary since concept v17; and a
 * company criterion nothing could assess is ratified as „nicht bewertbar" (the company matching model)
 * where the person side says „Zu prüfen". Each panel decides its own buckets and hands the result
 * in, which is why the dot takes a `tone` and not a criterion.
 */

/**
 * The three dot colours, and they are the person ranking's own: emerald for met, amber for anything
 * that needs a look, rose for something that was disproved.
 *
 * A DOT AND A TITLE, never a border or a background. State on these two surfaces is carried by
 * glyph and by text; the dot is the one chromatic exception the person ranking already established,
 * and it is a 6px dot rather than a rule on the row.
 */
const DOT_TONES = {
  met: "bg-emerald-500",
  /** Not assessable, partly met, or met only under a lenient check — all "look at this". */
  attention: "bg-ink-attention",
  unmet: "bg-rose-500",
} as const;

export type CriterionDotTone = keyof typeof DOT_TONES;

/** The same expandable AI explanation in both ranking drill-downs. */
export function CriterionAiReason({
  reason,
  open,
  onToggle,
}: {
  reason: string;
  open: boolean;
  onToggle: () => void;
}) {
  const intl = useIntl();
  const reasonId = useId();
  return (
    <div className="min-w-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? reasonId : undefined}
        onClick={onToggle}
        className="inline-flex shrink-0 items-center rounded-full bg-accent/10 px-2 py-0.5 text-smycles-micro font-semibold uppercase leading-none tracking-[0.12em] text-ink-accent ring-1 ring-accent-decoration/20 outline-none transition hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-ink-ui"
        title={intl.formatMessage({
          id: "match.result.ranking.aiChipTitle",
          defaultMessage: "Reason from the AI evaluation",
        })}
      >
        <FormattedMessage id="match.result.ranking.aiChip" defaultMessage="AI Reason" />
      </button>
      {open ? (
        <div id={reasonId} className="smycles-field-value mt-1 max-w-prose break-words leading-snug text-ink">
          {reason}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The status dot at the head of a criterion line.
 *
 * `title` is the caller's, and on both sides it is the very word the line prints for the same
 * criterion — so the tooltip and the visible verdict cannot drift apart.
 */
export function CriterionStatusDot({ tone, title }: { tone: CriterionDotTone; title: string }) {
  return (
    <span
      aria-hidden="true"
      title={title}
      className={`mt-[0.6rem] h-1.5 w-1.5 justify-self-center rounded-full ${DOT_TONES[tone]}`}
    />
  );
}

/**
 * The Muss/Kann badge: a closed lock for a hard requirement, an open one for a wish.
 *
 * Same metaphor as everywhere else in the match screens (`lock-icons`), and the accent fill is on
 * the MUSS badge only — it marks what KIND of requirement this is, which is a property of the
 * criterion and not a state of the assessment, so it is not the colour-for-state the design rules
 * reserve.
 */
export function CriterionRequirementGlyph({ isMust, label }: { isMust: boolean; label: string }) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition [&>svg]:h-3.5 [&>svg]:w-3.5 ${
        isMust ? "" : "text-ink-muted"
      }`}
      style={
        isMust ? { background: "var(--color-accent)", color: "var(--color-on-accent)" } : undefined
      }
      title={label}
      aria-label={label}
    >
      {isMust ? <LockClosedIcon /> : <LockOpenIcon />}
    </span>
  );
}

/**
 * The heading of one criterion-KIND group — its name, how many criteria are in it, and a chevron
 * that folds the group away.
 *
 * A button and not a summary element: the caller needs the click for its own bookkeeping too (the
 * person panel holds a scroll anchor across the height change), and `aria-expanded` says the same
 * thing to a screen reader that the chevron says to an eye.
 */
export function CriterionKindHeading({
  label,
  count,
  open,
  onToggle,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="group flex w-full items-center gap-1.5 border-b border-ink/10 pb-1 pt-2 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink-accent-ui"
    >
      <span className="smycles-field-label font-semibold text-ink-muted transition group-hover:text-ink-accent">
        {label}
      </span>
      <span className="text-smycles-label tabular-nums text-ink-muted">({count})</span>
      <span
        aria-hidden="true"
        className={`shrink-0 text-smycles-micro text-ink-muted transition-transform duration-[var(--duration-base)] group-hover:text-ink-accent ${
          open ? "rotate-90" : ""
        }`}
      >
        ›
      </span>
    </button>
  );
}

/** The requirement axis: everything, only the hard requirements, or only the wishes. */
export type CriterionRequirementFilterValue = "all" | "must" | "kann";

/**
 * The MUSS/KANN filter, as one segmented control: `Alle`, then a closed lock with the Muss count
 * and an open lock with the Kann count.
 *
 * The counts are the CALLER's, and on both sides they count what is inside the currently selected
 * outcome bucket rather than the whole row — the number then answers the question the reader is
 * actually asking ("how many of these are Muss?") instead of restating the row's own pills.
 *
 * The glyphs carry no words on purpose: the lock is the label, the number beside it is the fact,
 * and the wording lives in the `title` for anyone who needs it spelled out.
 */
export function CriterionRequirementFilter({
  value,
  onChange,
  mustCount,
  kannCount,
  className = "",
  testId,
}: {
  value: CriterionRequirementFilterValue;
  onChange: (value: CriterionRequirementFilterValue) => void;
  mustCount: number;
  kannCount: number;
  className?: string;
  /**
   * Optional handle for a test. Needed because the two lock glyphs deliberately carry no words: the
   * segments' accessible names are bare numbers, and the `title` they do carry is the same word the
   * lock badge on every criterion line carries, so "the Muss segment" is not addressable by text.
   */
  testId?: string;
}) {
  const intl = useIntl();
  return (
    <SegmentedControl<CriterionRequirementFilterValue>
      size="kpi"
      data-testid={testId}
      className={className}
      value={value}
      onChange={onChange}
      options={[
        {
          value: "all",
          label: intl.formatMessage({ id: "match.result.ranking.filterAll", defaultMessage: "Alle" }),
        },
        {
          value: "must",
          label: (
            <span
              className="inline-flex items-center gap-1 [&>svg]:h-3 [&>svg]:w-3"
              title={intl.formatMessage({
                id: "match.result.ranking.filterMust",
                defaultMessage: "Nur Muss-Kriterien",
              })}
            >
              <LockClosedIcon />
              <span className="tabular-nums">{mustCount}</span>
            </span>
          ),
        },
        {
          value: "kann",
          label: (
            <span
              className="inline-flex items-center gap-1 [&>svg]:h-3 [&>svg]:w-3"
              title={intl.formatMessage({
                id: "match.result.ranking.filterKann",
                defaultMessage: "Nur Kann-Kriterien",
              })}
            >
              <LockOpenIcon />
              <span className="tabular-nums">{kannCount}</span>
            </span>
          ),
        },
      ]}
    />
  );
}
